import type { ComponentProps, ReactNode } from "react";
import { useEffect } from "react";
import Tooltip, { TooltipContent, TooltipTrigger } from "../tooltip/tooltip";

/**
 * Detect the operating system to determine the appropriate Meta key symbol.
 * On macOS, Meta key is Command (⌘), on Windows it's Windows key (⊞).
 */
function detectOS(): "macos" | "windows" | "other" {
  if (typeof window === "undefined") return "other";

  const platform = navigator.platform.toLowerCase();
  const userAgent = navigator.userAgent.toLowerCase();

  // Check for macOS
  if (
    platform.includes("mac") ||
    platform === "iphone" ||
    userAgent.includes("mac os x")
  ) {
    return "macos";
  }

  // Check for Windows
  if (platform.includes("win") || userAgent.includes("windows")) {
    return "windows";
  }

  return "other";
}

/**
 * Get the Meta key symbol based on the operating system.
 * This is evaluated at module load time for performance.
 */
function getMetaKeySymbol(): string {
  const os = detectOS();
  // On macOS: Command key (⌘)
  // On Windows: Windows key (⊞)
  // On other platforms: Default to Command (⌘)
  return os === "windows" ? "⊞" : "⌘";
}

/**
 * Key configuration mapping consumer-friendly key names to:
 * - eventKey: The key name as it appears in KeyboardEvent.key
 * - symbol: The display symbol for the key (e.g., "⌘" for Command, "⇧" for Shift)
 */
const KEY_CONFIG = {
  // Modifier keys
  Shift: { eventKey: "Shift", symbol: "⇧" },
  Alt: { eventKey: "Alt", symbol: "⌥" },
  Meta: { eventKey: "Meta", symbol: getMetaKeySymbol() },
  Control: { eventKey: "Control", symbol: "⌃" },
  Ctrl: { eventKey: "Control", symbol: "⌃" },

  // Letters
  A: { eventKey: "a", symbol: "A" },
  B: { eventKey: "b", symbol: "B" },
  C: { eventKey: "c", symbol: "C" },
  D: { eventKey: "d", symbol: "D" },
  E: { eventKey: "e", symbol: "E" },
  F: { eventKey: "f", symbol: "F" },
  G: { eventKey: "g", symbol: "G" },
  H: { eventKey: "h", symbol: "H" },
  I: { eventKey: "i", symbol: "I" },
  J: { eventKey: "j", symbol: "J" },
  K: { eventKey: "k", symbol: "K" },
  L: { eventKey: "l", symbol: "L" },
  M: { eventKey: "m", symbol: "M" },
  N: { eventKey: "n", symbol: "N" },
  O: { eventKey: "o", symbol: "O" },
  P: { eventKey: "p", symbol: "P" },
  Q: { eventKey: "q", symbol: "Q" },
  R: { eventKey: "r", symbol: "R" },
  S: { eventKey: "s", symbol: "S" },
  T: { eventKey: "t", symbol: "T" },
  U: { eventKey: "u", symbol: "U" },
  V: { eventKey: "v", symbol: "V" },
  W: { eventKey: "w", symbol: "W" },
  X: { eventKey: "x", symbol: "X" },
  Y: { eventKey: "y", symbol: "Y" },
  Z: { eventKey: "z", symbol: "Z" },

  // Numbers
  "0": { eventKey: "0", symbol: "0" },
  "1": { eventKey: "1", symbol: "1" },
  "2": { eventKey: "2", symbol: "2" },
  "3": { eventKey: "3", symbol: "3" },
  "4": { eventKey: "4", symbol: "4" },
  "5": { eventKey: "5", symbol: "5" },
  "6": { eventKey: "6", symbol: "6" },
  "7": { eventKey: "7", symbol: "7" },
  "8": { eventKey: "8", symbol: "8" },
  "9": { eventKey: "9", symbol: "9" },

  // Special keys
  Enter: { eventKey: "Enter", symbol: "↵" },
  Escape: { eventKey: "Escape", symbol: "⎋" },
  Space: { eventKey: " ", symbol: "Space" },
  Tab: { eventKey: "Tab", symbol: "⇥" },
  Backspace: { eventKey: "Backspace", symbol: "⌫" },
  Delete: { eventKey: "Delete", symbol: "⌦" },
  ArrowUp: { eventKey: "ArrowUp", symbol: "↑" },
  ArrowDown: { eventKey: "ArrowDown", symbol: "↓" },
  ArrowLeft: { eventKey: "ArrowLeft", symbol: "←" },
  ArrowRight: { eventKey: "ArrowRight", symbol: "→" },
  Home: { eventKey: "Home", symbol: "⇱" },
  End: { eventKey: "End", symbol: "⇲" },
  PageUp: { eventKey: "PageUp", symbol: "⇞" },
  PageDown: { eventKey: "PageDown", symbol: "⇟" },
} as const;

export type KeyName = keyof typeof KEY_CONFIG;

/**
 * Get the event key name for a given key name
 */
function getEventKey(keyName: KeyName): string {
  return KEY_CONFIG[keyName].eventKey;
}

/**
 * Get the display symbol for a given key name
 */
function getKeySymbol(keyName: KeyName): string {
  return KEY_CONFIG[keyName].symbol;
}

/**
 * Normalize a key from KeyboardEvent.key to its canonical form for event matching
 */
function normalizeKey(key: string): string | null {
  // For single-letter keys, normalize to uppercase first to match KEY_CONFIG
  if (key.length === 1 && /[a-zA-Z]/.test(key)) {
    const upperKey = key.toUpperCase();
    if (upperKey in KEY_CONFIG) {
      return getEventKey(upperKey as KeyName);
    }
    // Fallback: return lowercase for direct matching
    return key.toLowerCase();
  }

  // Check if it's a direct match in KEY_CONFIG
  if (key in KEY_CONFIG) {
    return getEventKey(key as KeyName);
  }

  // Try case-insensitive match for other keys
  const capitalizedKey =
    key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
  if (capitalizedKey in KEY_CONFIG) {
    return getEventKey(capitalizedKey as KeyName);
  }

  return null;
}

type KeyboardShortcutProps = Omit<
  ComponentProps<typeof Tooltip>,
  "children"
> & {
  keys: readonly KeyName[];
  enabled?: boolean;
  children: ReactNode;
  onTrigger: () => void;
  shouldTrigger?: (event: KeyboardEvent) => boolean;
};

/**
 * Format keys for display in tooltips
 */
export function formatKeys(keys: readonly KeyName[]): string {
  return keys.map((key) => getKeySymbol(key)).join("");
}

/**
 * Check if a keyboard event matches the specified key combination.
 * @param event - The keyboard event to check
 * @param requiredKeys - The key combination to match (e.g., ["Shift", "Alt", "K"])
 * @returns True if the event matches the key combination, false otherwise
 */
export function matchesKeyCombination(
  event: KeyboardEvent,
  combination: readonly KeyName[],
): boolean {
  // Get keys for the required combination
  const keys = combination
    .map((key) => getEventKey(key))
    .filter((key): key is string => key !== null);
  const keysSet = new Set(keys);

  // Extract modifier keys and main key
  const requiresShift = keysSet.has("Shift");
  const requiresAlt = keysSet.has("Alt");
  const requiresMeta = keysSet.has("Meta");
  const requiresAltOrMeta = requiresAlt || requiresMeta;
  const requiresCtrl = keysSet.has("Control");

  // Find the main key (not a modifier)
  const modifierKeys = new Set(["Shift", "Alt", "Meta", "Control"]);
  const mainKey = keys.find((key) => !modifierKeys.has(key));

  if (!mainKey) return false;

  // Check if the pressed key matches the main key
  const pressedKey = normalizeKey(event.key);
  if (!pressedKey || pressedKey !== mainKey) return false;

  // Check modifier keys (these checks also ensure no extra modifiers are pressed)
  const shiftMatches = requiresShift === event.shiftKey;
  const altOrMetaMatches = requiresAltOrMeta
    ? event.altKey || event.metaKey
    : !event.altKey && !event.metaKey;
  const ctrlMatches = requiresCtrl === event.ctrlKey;

  return shiftMatches && altOrMetaMatches && ctrlMatches;
}

/**
 * KeyboardShortcut component that wraps Tooltip and adds keyboard shortcut functionality.
 * Listens for the specified key combination and calls onTrigger when pressed.
 */
export default function KeyboardShortcut({
  keys,
  children,
  onTrigger,
  enabled = true,
  shouldTrigger,
  ...props
}: KeyboardShortcutProps) {
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (!enabled) {
        return;
      }

      if (shouldTrigger && !shouldTrigger(event)) {
        return;
      }

      if (matchesKeyCombination(event, keys)) {
        event.preventDefault();
        event.stopPropagation();
        onTrigger();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [enabled, keys, onTrigger, shouldTrigger]);

  return <Tooltip {...props}>{children}</Tooltip>;
}

export const KeyboardShortcutTrigger = TooltipTrigger;
export const KeyboardShortcutContent = TooltipContent;
